import type {SetUrlFiltersOptions, UseUrlFiltersOptions} from './types';

import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

import {deserializeUrlFilters, serializeUrlFilters} from './utils';

export const useUrlFilters = <TFilters extends object>({
  schema
}: UseUrlFiltersOptions<TFilters>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const schemaKey = JSON.stringify(schema);
  const stableSchema = useMemo(() => JSON.parse(schemaKey) as typeof schema, [schemaKey]);
  const schemaKeys = useMemo(() => Object.keys(stableSchema), [stableSchema]);
  const filters = useMemo(
    () => deserializeUrlFilters<TFilters>(new URLSearchParams(searchParamsKey), stableSchema),
    [searchParamsKey, stableSchema]
  );

  const setFilters = useCallback(
    (nextFilters: TFilters, options?: SetUrlFiltersOptions) => {
      const nextSearchParams = new URLSearchParams(searchParamsKey);
      schemaKeys.forEach((key) => nextSearchParams.delete(key));
      serializeUrlFilters(nextFilters, stableSchema).forEach((value, key) =>
        nextSearchParams.append(key, value)
      );
      setSearchParams(nextSearchParams, {replace: options?.replace});
    },
    [schemaKeys, searchParamsKey, setSearchParams, stableSchema]
  );

  const clearFilters = useCallback(
    (keysToClear: keyof TFilters | readonly (keyof TFilters)[], options?: SetUrlFiltersOptions) => {
      const nextSearchParams = new URLSearchParams(searchParamsKey);
      const normalizedKeys = Array.isArray(keysToClear) ? keysToClear : [keysToClear];
      normalizedKeys.forEach((key) => nextSearchParams.delete(String(key)));
      setSearchParams(nextSearchParams, {replace: options?.replace});
    },
    [searchParamsKey, setSearchParams]
  );

  return {
    filters,
    setFilters,
    clearFilters
  };
};
